<template>
  <div>
    <!-- Header -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body class="d-flex align-items-center py-1">
        <h4 class="mb-0">
          <feather-icon
            icon="KeyIcon"
            size="20"
            class="text-primary mr-1"
          />
          API Anahtarları ({{ metaData.total }})
        </h4>
        <b-button
          variant="primary"
          class="ml-auto"
          @click="openCreateModal"
        >
          <feather-icon icon="PlusIcon" /> Yeni Anahtar
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Info banner -->
    <b-alert
      show
      variant="info"
      class="py-1 px-1 mb-2"
    >
      <small>
        <feather-icon icon="ShieldIcon" /> API key/secret AES-256-GCM ile şifrelenerek saklanır.
        Kaydedildikten sonra geri okunamaz; değiştirmek için yeniden gir.
        Live trading için <strong>Futures + Spot trading + Reading</strong> izinlerini ver
        (Withdraw izni KESİNLİKLE verme).
      </small>
    </b-alert>

    <!-- Table -->
    <b-card no-body>
      <b-table
        responsive="sm"
        :fields="fields"
        :items="dataList"
        :busy="loading"
        striped
        show-empty
        empty-text="Henüz API anahtarı eklenmedi."
      >
        <template #cell(provider)="data">
          <div class="font-weight-bold">
            {{ data.item.provider ? data.item.provider.name : '—' }}
          </div>
          <div class="font-small-3 text-muted">
            {{ data.item.provider ? data.item.provider.slug : '' }}
          </div>
        </template>
        <template #cell(label)="data">
          {{ data.item.label || '—' }}
        </template>
        <template #cell(isActive)="data">
          <b-badge :variant="data.item.isActive ? 'success' : 'secondary'">
            {{ data.item.isActive ? 'Aktif' : 'Pasif' }}
          </b-badge>
        </template>
        <template #cell(createdAt)="data">
          <span class="font-small-3">{{ formatDateTime(data.item.createdAt) }}</span>
        </template>
        <template #cell(actions)="data">
          <div class="text-right">
            <b-dropdown
              variant="outline-info"
              size="sm"
              class="mr-1"
              :disabled="testingId === data.item.id"
              right
            >
              <template #button-content>
                <b-spinner
                  v-if="testingId === data.item.id"
                  small
                />
                <feather-icon
                  v-else
                  icon="WifiIcon"
                /> Test
              </template>
              <b-dropdown-item @click="onTest(data.item, 'FUTURES')">
                Futures bağlantısı
              </b-dropdown-item>
              <b-dropdown-item @click="onTest(data.item, 'SPOT')">
                Spot bağlantısı
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              variant="outline-primary"
              size="sm"
              class="mr-1"
              @click="openEditModal(data.item)"
            >
              <feather-icon icon="Edit2Icon" />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="onRemove(data.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner small /> Yükleniyor...
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- ─── Create / Edit Modal ────────────────────────────────────────────── -->
    <b-modal
      v-model="editorModal.show"
      :title="editorModal.id ? 'API Anahtarı Düzenle' : 'Yeni API Anahtarı'"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Provider *</label>
          <b-form-select
            v-model="form.providerId"
            :options="providerOptions"
            :disabled="!!editorModal.id"
          />
          <small
            v-if="editorModal.id"
            class="text-muted"
          >Provider değiştirilemez — sil ve yenisini ekle.</small>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Etiket</label>
          <b-form-input
            v-model="form.label"
            placeholder="örn: Ana hesap, test hesabı"
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">
            API Key {{ editorModal.id ? '(boş bırakırsan değişmez)' : '*' }}
          </label>
          <b-form-input
            v-model="form.apiKey"
            type="password"
            autocomplete="new-password"
            placeholder="..."
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">
            API Secret {{ editorModal.id ? '(boş bırakırsan değişmez)' : '*' }}
          </label>
          <b-form-input
            v-model="form.apiSecret"
            type="password"
            autocomplete="new-password"
            placeholder="..."
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="form.isActive"
            switch
          >
            <strong>Aktif</strong> — pasif anahtarlar bot tarafından kullanılmaz.
          </b-form-checkbox>
        </b-col>
      </b-row>
      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="editorModal.show = false"
        >
          İptal
        </b-button>
        <b-button
          variant="primary"
          :disabled="!canSubmit"
          @click="onSubmit"
        >
          <feather-icon icon="CheckIcon" /> {{ editorModal.id ? 'Kaydet' : 'Ekle' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BTable, BButton, BBadge, BSpinner,
  BFormInput, BFormSelect, BFormCheckbox,
  BRow, BCol, BModal, BAlert,
  BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ApiKeysIndex',
  components: {
    BCard,
    BCardBody,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BRow,
    BCol,
    BModal,
    BAlert,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      loading: false,
      fields: [
        { key: 'id', label: '#', thClass: 'width-50' },
        { key: 'provider', label: 'Provider' },
        { key: 'label', label: 'Etiket' },
        { key: 'isActive', label: 'Durum', thClass: 'width-100 text-center' },
        { key: 'createdAt', label: 'Eklenme', thClass: 'width-160' },
        { key: 'actions', label: '', thClass: 'width-140 text-nowrap' },
      ],
      editorModal: { show: false, id: null },
      form: {
        providerId: null, label: '', apiKey: '', apiSecret: '', isActive: true,
      },
      testingId: null,
    }
  },
  computed: {
    dataList() { return this.$store.getters['apiKeys/dataList'] },
    metaData() { return this.$store.getters['apiKeys/metaData'] },
    providers() { return this.$store.getters['apiKeys/providers'] },
    saveStatus() { return this.$store.getters['apiKeys/dataSaveStatus'] },
    providerOptions() {
      return [
        { value: null, text: '— seç —' },
        ...(this.providers || []).map(p => ({ value: p.id, text: `${p.name} (${p.kind})` })),
      ]
    },
    canSubmit() {
      if (!this.form.providerId) return false
      if (this.editorModal.id) {
        // Edit: providerId zaten kilitli; key/secret opsiyonel ama eğer girildiyse min 8
        const k = this.form.apiKey || ''
        const s = this.form.apiSecret || ''
        if (k && k.trim().length < 8) return false
        if (s && s.trim().length < 8) return false
        return true
      }
      // Create: zorunlu
      return (this.form.apiKey || '').trim().length >= 8
        && (this.form.apiSecret || '').trim().length >= 8
    },
  },
  watch: {
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Başarılı', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
  },
  created() {
    this.fetchList()
    this.$store.dispatch('apiKeys/getProviders')
  },
  methods: {
    async fetchList() {
      this.loading = true
      await this.$store.dispatch('apiKeys/getDataList', {})
      this.loading = false
    },
    openCreateModal() {
      this.form = {
        providerId: null, label: '', apiKey: '', apiSecret: '', isActive: true,
      }
      this.editorModal = { show: true, id: null }
    },
    openEditModal(item) {
      this.form = {
        providerId: item.id_providers,
        label: item.label || '',
        apiKey: '',
        apiSecret: '',
        isActive: !!item.isActive,
      }
      this.editorModal = { show: true, id: item.id }
    },
    async onSubmit() {
      const body = {
        label: this.form.label || null,
        isActive: !!this.form.isActive,
      }
      if (this.form.apiKey && this.form.apiKey.trim()) body.apiKey = this.form.apiKey.trim()
      if (this.form.apiSecret && this.form.apiSecret.trim()) body.apiSecret = this.form.apiSecret.trim()
      if (!this.editorModal.id) body.providerId = this.form.providerId
      try {
        if (this.editorModal.id) {
          await this.$store.dispatch('apiKeys/update', { id: this.editorModal.id, body })
        } else {
          await this.$store.dispatch('apiKeys/create', body)
        }
        this.editorModal.show = false
      } catch (e) { /* toast already shown */ }
    },
    async onTest(item, market) {
      this.testingId = item.id
      try {
        await this.$store.dispatch('apiKeys/testConnection', { id: item.id, market })
      } catch (e) { /* toast already shown */ } finally {
        this.testingId = null
      }
    },
    async onRemove(id) {
      this.$swal({
        title: 'API anahtarı silinsin mi?',
        text: 'Bu anahtarı kullanan bot/işlemler etkilenebilir.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sil',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) await this.$store.dispatch('apiKeys/remove', id)
      })
    },
    formatDateTime(v) {
      if (!v) return '—'
      try {
        const d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleString('tr-TR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
      } catch (e) { return String(v) }
    },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },
  },
}
</script>
